import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { roleService } from "../../services/api/role.service";

export const fetchRoles = createAsyncThunk(
  "role/fetchRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await roleService.getRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch roles"
      );
    }
  }
);

export const fetchAllRoles = createAsyncThunk(
  "role/fetchAllRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await roleService.getRoles();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all roles"
      );
    }
  }
);

export const getUserTypes = createAsyncThunk(
  "role/getUserTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await roleService.getUserTypes();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user types"
      );
    }
  }
);

export const fetchRoleMenus = createAsyncThunk(
  "role/fetchRoleMenus",
  async (_, { rejectWithValue }) => {
    try {
      // debugger;
      const response = await roleService.getRoleMenus();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch menus"
      );
    }
  }
);

export const fetchRolePermissions = createAsyncThunk(
  "role/fetchRolePermissions",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await roleService.getRolePermissions(roleId);
      // httpClient returns axios response: response.data = { success, data, ... }
      // Backend ResponseBuilder.single() wraps data in array: { data: [{ role, permissions }] }
      // So response.data.data is the array we need
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch role permissions"
      );
    }
  }
);

// New method specifically for bulk upload template generation
export const fetchRolePermissionsForBulkUpload = createAsyncThunk(
  "role/fetchRolePermissionsForBulkUpload",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await roleService.getRolePermissions(roleId);
      // API response structure: { success, statusCode, message, data: [{ role, permissions }], timestamp }
      // response is axios response, so response.data is the API response
      // response.data.data is the actual array: [{ role, permissions }]
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch role permissions for bulk upload"
      );
    }
  }
);
export const deleteRole = createAsyncThunk(
  "role/deleteRole",
  async (roleId, { rejectWithValue }) => {
    try {
      await roleService.deleteRole(roleId);
      return roleId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete role"
      );
    }
  }
);

export const updateRoleById = createAsyncThunk(
  "role/updateRoleById",
  async ({ roleId, roleData }, { rejectWithValue }) => {
    try {
      const response = await roleService.updateRole(roleId, roleData);
      return response.data; // backend returns updated role
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update role"
      );
    }
  }
);

export const activateRole = createAsyncThunk(
  "role/activateRole",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await roleService.activateRole(roleId);
      return { roleId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to activate role"
      );
    }
  }
);

export const deactivateRole = createAsyncThunk(
  "role/deactivateRole",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await roleService.deactivateRole(roleId);
      return { roleId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to deactivate role"
      );
    }
  }
);

const initialState = {
  roles: [],
  userTypes: [],
  menus: [],
  permissions: [],
  currentRole: null,
  isLoading: false,
  error: null,
  totalCount: 0,
};

const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentRole: (state, action) => {
      state.currentRole = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        // Filter only active roles
        const allRoles = action.payload.data || [];
        state.roles = allRoles.filter(role => role.is_active === true);
        // state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch all roles (including inactive) - for view/modify page
      .addCase(fetchAllRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        // Return all roles without filtering
        state.roles = action.payload.data || [];
      })
      .addCase(fetchAllRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Get user types
      .addCase(getUserTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userTypes = action.payload.data || [];
      })
      .addCase(getUserTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // New fetchRoleMenus reducers
      .addCase(fetchRoleMenus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRoleMenus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.menus = action.payload;
      })
      .addCase(fetchRoleMenus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Permissions
      .addCase(fetchRolePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRolePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        // state.permissions = action.payload.data[0].permissions;
        state.permissions = action.payload[0]?.permissions || [];
        // state.currentRole = action.payload.data[0].role;
        state.currentRole = action.payload[0]?.role || null;
      })
      .addCase(fetchRolePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.roles = state.roles.filter(
          (role) => role.role_id !== action.payload.roleId
        );
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.error = action.payload;
      })

      // To update the role
      .addCase(updateRoleById.fulfilled, (state, action) => {
        state.roles = state.roles.map((role) =>
          role.role_id === action.payload.role_id ? action.payload : role
        );
      })

      // Activate role
      .addCase(activateRole.fulfilled, (state, action) => {
        state.roles = state.roles.map((role) =>
          role.role_id === action.payload.roleId
            ? { ...role, is_active: true }
            : role
        );
      })
      .addCase(activateRole.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Deactivate role
      .addCase(deactivateRole.fulfilled, (state, action) => {
        state.roles = state.roles.map((role) =>
          role.role_id === action.payload.roleId
            ? { ...role, is_active: false }
            : role
        );
      })
      .addCase(deactivateRole.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentRole } = roleSlice.actions;
export default roleSlice.reducer;
